/**
 * MonthlyChart Component
 * Line chart spesa mensile (luce + gas) ultimi 12 mesi
 */

import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function MonthlyChart({ bollette }) {
  const chartData = useMemo(() => {
    // Helper: parse DD/MM/YYYY string to Date object
    const parseDDMMYYYY = (dateStr) => {
      if (!dateStr || typeof dateStr !== 'string') return null;
      const parts = dateStr.split('/');
      if (parts.length !== 3) return null;
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
      return new Date(year, month - 1, day);
    };

    // Get last 12 months
    const now = new Date();
    const last12Months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last12Months.push({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        label: d.toLocaleDateString('it-IT', { year: 'numeric', month: 'short' })
      });
    }

    // Initialize month-year keys for tracking
    const monthKeys = {};
    last12Months.forEach(m => {
      const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
      monthKeys[key] = { luce: 0, gas: 0 };
    });

    // Distribute each bolletta across its coverage months
    bollette.forEach(b => {
      let startDate = parseDDMMYYYY(b.periodo_inizio);
      let endDate = parseDDMMYYYY(b.periodo_fine);

      // Fallback to data_bolletta or data if periodo dates missing
      if (!startDate || !endDate) {
        const fallbackDate = b.data_bolletta
          ? new Date(b.data_bolletta)
          : parseDDMMYYYY(b.data);
        if (fallbackDate) {
          startDate = endDate = fallbackDate;
        } else {
          return; // Skip if no valid date found
        }
      }

      const importoPerMonth = parseFloat(b.importo) / (b.periodo_mesi || 1);

      // Iterate from start month to end month (inclusive)
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        const key = `${year}-${String(month).padStart(2, '0')}`;

        // Only add to months in last12Months range
        if (monthKeys[key]) {
          if (b.tipo === 'luce') {
            monthKeys[key].luce += importoPerMonth;
          } else if (b.tipo === 'gas') {
            monthKeys[key].gas += importoPerMonth;
          }
        }

        // Move to next month
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
    });

    // Build monthlyData from last12Months in order
    const monthlyData = last12Months.map(m => {
      const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
      const data = monthKeys[key];
      return {
        label: m.label,
        luce: data.luce,
        gas: data.gas,
        total: data.luce + data.gas
      };
    });

    return {
      labels: monthlyData.map(d => d.label),
      datasets: [
        {
          label: 'Totale (Luce + Gas)',
          data: monthlyData.map(d => d.total),
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
        },
        {
          label: 'Luce 💡',
          data: monthlyData.map(d => d.luce),
          borderColor: '#ffc107',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          borderWidth: 1.5,
          tension: 0.3,
          borderDash: [5, 5],
        },
        {
          label: 'Gas 🔥',
          data: monthlyData.map(d => d.gas),
          borderColor: '#f44336',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
          borderWidth: 1.5,
          tension: 0.3,
          borderDash: [5, 5],
        },
      ],
      monthlyData, // For tooltip
    };
  }, [bollette]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            const value = context.parsed.y;
            if (context.datasetIndex === 0) {
              const idx = context.dataIndex;
              const data = chartData.monthlyData[idx];
              return [
                `Totale: €${value.toFixed(2)}`,
                `  Luce: €${data.luce.toFixed(2)}`,
                `  Gas: €${data.gas.toFixed(2)}`
              ];
            }
            return `${context.dataset.label}: €${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `€${value}`
        }
      }
    }
  };

  if (bollette.length === 0) {
    return (
      <div style={{ 
        height: '300px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#999',
        border: '2px dashed #ddd',
        borderRadius: '8px'
      }}>
        <p>📊 Nessun dato disponibile per il grafico</p>
      </div>
    );
  }

  return (
    <div style={{ height: '350px', position: 'relative' }}>
      <Line data={chartData} options={options} />
    </div>
  );
}

export default MonthlyChart;
